import { useEffect, useRef, useState } from 'react';
import {
  alvoEmMovimento,
  encontrarAlvo,
  medirAlvo,
  mesmoRetangulo,
  PACIENCIA_PADRAO_MS,
  QUADROS_PARA_ASSENTAR,
  TETO_PARA_ASSENTAR_MS,
} from './alvo';

/**
 * Segue o retângulo do alvo em coordenadas da janela, a cada quadro.
 *
 * Status: `procurando` (a tela pode estar montando ou o dado chegando),
 * `achado` (o alvo existe e parou de se mexer) ou `ausente` (procurou o
 * bastante e não existe; o nível de acesso pode não ter aquela tela).
 *
 * Medir por quadro, e não por evento de scroll/resize, pega também o que não
 * avisa: a aba que entra animada, o menu lateral abrindo, a tabela que chega
 * do servidor. Do `ausente` ele volta sozinho a `achado` se o alvo aparecer.
 *
 * Enquanto procura o alvo novo, o retângulo do passo anterior fica: zerar ali
 * escurecia a tela inteira por um quadro a cada troca de passo. Só `ausente`
 * apaga o recorte.
 *
 * `aoAssentar` roda uma vez, quando o alvo para, antes da medida que vai para
 * a tela: é onde o alvo é rolado para a vista.
 */
export const useRetanguloDoAlvo = (alvo, paciencia = PACIENCIA_PADRAO_MS, aoAssentar) => {
  const [estado, setEstado] = useState({ rect: null, status: 'procurando', alvo: null });
  const aoAssentarRef = useRef(aoAssentar);
  aoAssentarRef.current = aoAssentar;

  useEffect(() => {
    if (!alvo) {
      setEstado({ rect: null, status: 'procurando', alvo: null });
      return undefined;
    }

    let quadro = 0;
    let cancelado = false;
    let avistadoEm = null;
    let ultimaMedida = null;
    let quadrosParado = 0;
    let assentado = false;
    const desde = performance.now();
    setEstado((anterior) =>
      anterior.status === 'procurando' ? anterior : { ...anterior, status: 'procurando' }
    );

    const medir = () => {
      if (cancelado) return;
      const el = encontrarAlvo(alvo);
      const agora = performance.now();
      let proximo = null;
      let status;

      if (!el) {
        avistadoEm = null;
        ultimaMedida = null;
        quadrosParado = 0;
        assentado = false;
        status = agora - desde >= paciencia ? 'ausente' : 'procurando';
      } else {
        proximo = medirAlvo(el);
        if (!assentado) {
          avistadoEm ??= agora;
          quadrosParado = mesmoRetangulo(proximo, ultimaMedida) ? quadrosParado + 1 : 0;
          ultimaMedida = proximo;
          assentado =
            (quadrosParado >= QUADROS_PARA_ASSENTAR && !alvoEmMovimento(el)) ||
            agora - avistadoEm >= TETO_PARA_ASSENTAR_MS;
          if (assentado && aoAssentarRef.current) {
            aoAssentarRef.current(el);
            proximo = medirAlvo(el);
          }
        }
        status = assentado ? 'achado' : 'procurando';
      }

      setEstado((anterior) => {
        const rect = status === 'achado' ? proximo : status === 'ausente' ? null : anterior.rect;
        return anterior.status === status && anterior.alvo === alvo && mesmoRetangulo(anterior.rect, rect)
          ? anterior
          : { rect, status, alvo };
      });
      quadro = requestAnimationFrame(medir);
    };
    quadro = requestAnimationFrame(medir);

    return () => {
      cancelado = true;
      cancelAnimationFrame(quadro);
    };
  }, [alvo, paciencia]);

  // Na renderização em que o passo troca, o efeito ainda não rodou e o estado
  // é do alvo anterior: ele não pode valer como `achado` para o passo novo.
  return {
    rect: estado.rect,
    status: estado.alvo === alvo ? estado.status : 'procurando',
  };
};
